import { clsx, type ClassValue } from "clsx";
import saveAs from "file-saver";
import { twMerge } from "tailwind-merge";
import { Figure, FiguresAsObject } from "@/api/types";
import { dateFormatter } from "@/utils/date";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function htmlToText(html: string): string {
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*/gi, "\n")
    .replace(/<\/div>\s*/gi, "\n");
  const doc = new DOMParser().parseFromString(withBreaks, "text/html");
  return (doc.body.textContent ?? "").trim().replace(/\n{2,}/g, "\n");
}

export const getFigureDate = (
  figures: FiguresAsObject | undefined,
  code: string,
  toDate?: boolean
) => {
  if (toDate && figures?.latestValues[code]?.date)
    return new Date(figures?.latestValues[code]?.date);
  return dateFormatter(figures?.latestValues[code]?.date);
};
export const getFigureDateNumber = (
  figures: FiguresAsObject | undefined,
  code: string
) => {
  return getTimeFromDateString(figures?.latestValues[code]?.date);
};

export const formatFigureDate = (date: number[] | undefined) => {
  if (!date) return "";
  return `${date[0]}-${String(date[1]).padStart(2, "0")}-${String(
    date[2]
  ).padStart(2, "0")}`;
};

export const figureDateToString = (date: number[] | undefined) => {
  const d = formatFigureDate(date);
  return dateFormatter(d);
};

export const getTimeFromDateString = (date: string | undefined) => {
  if (!date) return undefined;
  const d = new Date(date).getTime();
  return isNaN(d) ? undefined : d;
};

export const SortFigureDates = (a: Figure, b: Figure, ascending = true) => {
  if (ascending)
    return (
      new Date(formatFigureDate(a.date)).getTime() -
      new Date(formatFigureDate(b.date)).getTime()
    );
  return (
    new Date(formatFigureDate(b.date)).getTime() -
    new Date(formatFigureDate(a.date)).getTime()
  );
};

export const getFigureString = (
  figures: FiguresAsObject | undefined,
  code: string
) => {
  if (!figures) return "";
  if (!figures.latestValues) return "";
  if (!figures.latestValues[code]) return "";
  return figures.latestValues[code].value;
};

export const getFigureBoolean = (
  figures: FiguresAsObject | undefined,
  code: string
) => {
  if (!figures) return false;
  if (!figures.latestValues) return false;
  if (!figures.latestValues[code]) return false;
  return !!figures?.latestValues[code]?.value;
};

export const saveDocument = (
  base: string,
  mimeType: string,
  filename: string
) => {
  const byteCharacters = atob(base);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  saveAs(blob, filename);
};
